import React, { useEffect, useState } from 'react';
import { AppointmentsProps } from '../appointments.interface';
import Style from './AppointmentCard.module.css';
import moment from 'moment';
import MapIcon from '../../icons/MapIcon';
import LockIcon from '../../icons/LockIcon';
import ThreeDotsIcon from '../../icons/ThreeDots';
import { useNavigate } from 'react-router-dom';
import { isAccessible, ToastType } from '../Appointments';
import SwiperButton from '../../SwiperButton/SwiperButton';
import UpcomingBedge from '../../../images/upcoming.svg';
import ExpiredBedge from '../../../images/expired.svg';
import MissedBedge from '../../../images/missed.svg';
import { AppDispatch } from '../../../redux/rootReducer';
import { useDispatch } from 'react-redux';
import { getAccessCode } from '../../../redux/appointment/actions/appointment.action';
import Toast from '../../toast/Toast';
import { useGeolocation } from '../../../hooks/geoLocation';
import ToggleSwitch from '../../SwipeButton/ToggleSwitch';
import OpenViaIcon from '../../icons/OpenViaIcon';

interface AppointmentCardProps {
    appointment: AppointmentsProps;
    background?: string;
    onMenuClick?: () => void;
    onAmazonDoor?: () => void;
    onEditAppointment?: () => void;
    onDeleteAppointment?: () => void;
    showEditAppointment?: boolean;
    showDeleteAppointment?: boolean;
    editAppointmentText?: string;
    deleteAppointmentText?: string;
    loading?: boolean;
    openLocationSettings: () => void;
    updateEntryExitCount: (appointmentId: string, entryExitCount: number) => void;
}

export interface AppointmentAccessCodeProps {
    appointmentId: string;
    latitude: string;
    longitude: string;
    locationId: string;
    unitNumber: string;
}

const AppointmentCard = ({
    appointment,
    background,
    onMenuClick,
    onAmazonDoor,
    onEditAppointment,
    onDeleteAppointment,
    showEditAppointment = true,
    showDeleteAppointment = true,
    editAppointmentText = 'Reschedule',
    deleteAppointmentText = 'Cancel',
    loading = false,
    openLocationSettings,
    updateEntryExitCount,
}: AppointmentCardProps) => {
    const [accessCode, setAccessCode] = useState<null | string>(null);
    const { coordinates, isEnabled, askForPermissionIfNeeded, error: geoLocationError, isSupported } = useGeolocation();
    const [showToast, setShowToast] = useState(false);
    const [toastType, setToastType] = useState<ToastType>('error');
    const [toastMessage, setToastMessage] = useState('');
    const dispatch: AppDispatch = useDispatch();
    const [isAPILoading, setIsAPILoading] = useState(false);

    const trackAppointment = () => {
        const oldAppointments: AppointmentsProps[] = JSON.parse(localStorage.getItem('currentAppointments') || '[]');
        const isExist = oldAppointments?.findIndex(
            (oldAppointment: AppointmentsProps) => oldAppointment?.appointmentId === appointment?.appointmentId,
        );
        if (isExist === -1) {
            oldAppointments.push(appointment);
            localStorage.setItem('currentAppointments', JSON.stringify(oldAppointments));
            window.dispatchEvent(new Event('localStorageUpdated'));
        }
    };

    const onKioskSuccess = async (accessCode: string) => {
        setAccessCode(accessCode);
        setIsAPILoading(false);
        if (appointment?.alertNotificationCount === 0) {
            await trackAppointment();
        }
    };

    const onBMSSuccess = ({
        message,
        appointmentId,
        entryExitCount,
    }: {
        message: string;
        entryExitCount: number;
        appointmentId: string;
    }) => {
        setIsAPILoading(false);
        setToastMessage(message);
        setToastType('success');
        setShowToast(true);
        updateEntryExitCount(appointmentId, entryExitCount);
    };

    const onFailure = (message: string) => {
        setIsAPILoading(false);
        setToastMessage(message);
        setShowToast(true);
        setToastType('error');
    };

    useEffect(() => {
        if (showToast) {
            setTimeout(() => {
                setShowToast(false);
            }, 4000);
        }
    }, [showToast]);

    const getAppointmentAccessCode = async () => {
        if (isSupported && isEnabled && coordinates?.latitude && coordinates?.longitude) {
            const appointmentAccessCodeData: AppointmentAccessCodeProps = {
                appointmentId: appointment.appointmentId,
                latitude: coordinates.latitude.toString(),
                longitude: coordinates.longitude.toString(),
                locationId: appointment?.locationId,
                unitNumber: appointment?.unitId,
            };
            setIsAPILoading(true);
            dispatch(getAccessCode(appointmentAccessCodeData, onKioskSuccess, onBMSSuccess, onFailure));
        }
        if (!isEnabled && geoLocationError) {
            openLocationSettings();
        }
        if (isSupported && !isEnabled) {
            const isAllowed = await askForPermissionIfNeeded();
            if (coordinates?.latitude && coordinates?.longitude && isAllowed) {
                const appointmentAccessCodeData: AppointmentAccessCodeProps = {
                    appointmentId: appointment.appointmentId,
                    latitude: coordinates.latitude.toString(),
                    longitude: coordinates.longitude.toString(),
                    locationId: appointment?.locationId,
                    unitNumber: appointment?.unitId,
                };
                setIsAPILoading(true);
                dispatch(getAccessCode(appointmentAccessCodeData, onKioskSuccess, onBMSSuccess, onFailure));
            }
            if (!isAllowed) {
                openLocationSettings();
            }
        }
    };

    const navigate = useNavigate();
    const appointmentStatus = isAccessible(
        appointment?.appointmentDate,
        appointment?.appointmentStartTime,
        appointment?.appointmentEndTime,
    );

    const onOpenVia = (slug: string) => {
        window.open(`https://app.openvia.co/credentials/${slug}`, '_blank', 'noopener,noreferrer');
    };

    return (
        <div className={`${Style.appointmentCard} appointmentCard`} style={{ backgroundColor: background ? background : '#F6F6F6' }}>
            {appointmentStatus !== 1 && (
                <div className={Style.BedgeContainer}>
                    <img
                        src={appointmentStatus === 0 ? UpcomingBedge : appointment?.entryExitCount === 0 ? MissedBedge : ExpiredBedge}
                        alt="bedge"
                        width={55}
                        height={44}
                    />
                </div>
            )}
            <div className={Style.upperContainer}>
                {/* <div className={Style.iconWrapper} onClick={onMenuClick && onMenuClick}>
                    <ThreeDotsIcon />
                </div> */}

                <div className={Style.detailWrapper}>
                    <p className={Style.title}>Location name</p>
                    <a className={Style.info} href={`/location/${appointment?.locationId}`} target="_blank" rel="noopener noreferrer">
                        {appointment.locationName}
                    </a>
                </div>

                <div className={Style.detailWrapper}>
                    <p className={Style.title}>Unit name</p>
                    <p className={Style.info}>
                        {appointment?.buildingName ? `${appointment?.buildingName} - ` : ''}
                        {appointment.unitYardiId ? appointment.unitYardiId : appointment.unitName}
                    </p>
                </div>

                <div className={Style.inlineItems}>
                    <div className={Style.detailWrapper}>
                        <p className={Style.title}>Date</p>
                        <p className={Style.info}>{moment(appointment.appointmentDate).format('MM-DD-YYYY')}</p>
                    </div>

                    <div className={Style.detailWrapper}>
                        <p className={Style.title}>Time</p>
                        <p className={Style.info}>
                            {moment(appointment.appointmentStartTime, 'hh:mm A').format('h:mm A')} -{' '}
                            {moment(appointment.appointmentEndTime, 'hh:mm A').format('h:mm A')}
                        </p>
                    </div>
                </div>
                <div className={Style.appointmentActions}>
                    {showEditAppointment && appointmentStatus !== -1 && (
                        <button className={Style.editButton} onClick={onEditAppointment}>
                            {editAppointmentText}
                        </button>
                    )}
                    {showDeleteAppointment && (
                        <button className={Style.deleteButton} onClick={onDeleteAppointment}>
                            {deleteAppointmentText}
                        </button>
                    )}
                </div>


                {appointment?.openViaSlug && (
                    <div
                        className={`${Style.openViaContainer} ${appointment.openViaSlug === null ? Style.disabled : ''}`}
                        onClick={() => {
                            if (appointment?.openViaSlug) {
                                onOpenVia(appointment?.openViaSlug);
                            }
                        }}>
                        <span>Click here to open the exterior door/gate</span>
                        <OpenViaIcon fill={appointment.openViaSlug === null ? '#D1D2D3' : '#3674EE'} width={20} height={20} />
                    </div>
                )}

                {appointment.amazonKeyAccessId !== null && appointmentStatus === 1 && (
                    <div>
                        <SwiperButton loading={loading} onDragend={() => onAmazonDoor?.()} />
                    </div>
                )}
            </div>

            {appointmentStatus !== -1 && (
                <div
                    className={`${Style.lowerContainer} ${(appointment.amazonKeyAccessId && appointment?.amazonKeyAccessCount > 0) || !appointment.amazonKeyAccessId
                        ? Style.show
                        : ''
                        } `}>
                    {
                        <div className={Style.infoBox}>
                            {appointment?.lockerName?.toLowerCase() === 'kiosk' ? (
                                <>
                                    <div className={Style.leftContainer}>
                                        <LockIcon />
                                    </div>
                                    <div className={Style.rightContainer}>
                                        <p>Kiosk Code</p>
                                        {
                                            <p
                                                className={`${appointmentStatus !== 1 ? Style.noStyle : ''}`}
                                                onClick={() => {
                                                    appointmentStatus === 1 && getAppointmentAccessCode();
                                                }}>
                                                {appointmentStatus === 1 ? (accessCode ? accessCode : `Tap Here`) : 'N/A'}
                                            </p>
                                        }
                                    </div>
                                </>
                            ) : (
                                <ToggleSwitch
                                    marginLeft={16}
                                    checked={appointment?.entryExitCount % 2 !== 0 ? true : false}
                                    onChange={() => {
                                        if (appointmentStatus === 1) {
                                            getAppointmentAccessCode();
                                        } else if (appointmentStatus === 0) {
                                            setShowToast(true);
                                            setToastMessage('Your appointment is not started yet.');
                                            setToastType('error');
                                        } else {
                                            setShowToast(true);
                                            setToastMessage('Your appointment is expired.');
                                            setToastType('error');
                                        }
                                    }}
                                    disabled={appointmentStatus !== 1}
                                    loading={isAPILoading}
                                />
                            )}
                        </div>
                    }

                    <div className={Style.infoBox}>
                        <div className={Style.leftContainer}>
                            <MapIcon stroke="#fff" />
                        </div>
                        <div className={Style.rightContainer}>
                            <p>I want to</p>
                            <p onClick={() => navigate(`/map-view/${appointment?.appointmentId}`)}>Navigate my tour.</p>
                        </div>
                    </div>
                </div>
            )}

            {showToast && <Toast message={toastMessage} type={toastType || 'error'} />}
        </div>
    );
};

export default AppointmentCard;
